import {
	InsightError,
	InsightRunProgress,
} from '../interfaces/insight.interface';
import { KristaEventFetch } from '../interfaces/krista-event.interface';
import {
	KristaGenerateResponse,
	KristaResponseStatus,
} from '../interfaces/krista-response.interface';
import { toAppKristaGenerateResponse } from './insight-response.utils';

export type KristaPollDecision =
	| { outcome: 'pending'; progress?: InsightRunProgress | null }
	| { outcome: 'resubmit' }
	| { outcome: 'failed'; error: InsightError }
	| {
			outcome: 'ready';
			response: KristaGenerateResponse;
	  };

/** Stop polling after this many Krista reads. */
export const InsightPollMaxAttempts = 30;

/**
 * A submit claim with no event id older than this can be retried.
 * Matches the insights function timeout.
 */
export const InsightSubmitClaimMillis = 5 * 60 * 1000;

const KristaResponseStatuses: readonly KristaResponseStatus[] = [
	'generated',
	'generated_with_caveat',
	'skipped_immaterial',
	'skipped',
	'declined',
	'pass_2_waiting',
];

/** Finished without commentary that can be stored. */
const TerminalWithoutNarrative: readonly KristaResponseStatus[] = [
	'declined',
	'skipped',
	'skipped_immaterial',
];

/** Not-ready or overloaded. A later poll may succeed. */
const RetryablePollStatuses = new Set([404, 408, 409, 425, 429]);

/**
 * Krista GET /events/{eventId}. POST only returns the id.
 *
 * While the run is in progress the body is `RECEIVED` or `RUNNING`,
 * `response` is null, and `progress` may describe the stage. A finished
 * run is `COMPLETED` and `response` is a JSON string. Parse that string
 * and trust `response.status`: `generated`, `generated_with_caveat`,
 * `skipped_immaterial`, `declined`, or `pass_2_waiting`.
 *
 * `pass_2_waiting` and an `UNKNOWN` event both need the same payload
 * submitted again for a fresh id. Retryable HTTP statuses stay pending
 * until the attempt cap.
 */
export function decideKristaPoll(
	fetch: KristaEventFetch,
	pollAttempts = 0,
): KristaPollDecision {
	const responseStatus = readKristaResponseStatus(fetch.body);
	if (
		responseStatus === 'pass_2_waiting' ||
		isUnknownKristaEvent(fetch.body)
	) {
		return resubmitOrGiveUp(pollAttempts);
	}

	if (!fetch.ok) {
		if (
			isRetryablePollStatus(fetch.status) &&
			pollAttempts < InsightPollMaxAttempts
		) {
			return pendingDecision(fetch.body);
		}
		return {
			outcome: 'failed',
			error: {
				code: String(fetch.status),
				message: fetch.message?.trim() || 'Krista poll failed',
			},
		};
	}

	const response = readKristaGenerateResponse(fetch.body);
	if (response) return { outcome: 'ready', response };

	if (pollAttempts >= InsightPollMaxAttempts) {
		return {
			outcome: 'failed',
			error: {
				code: null,
				message: 'Insight polling timed out',
			},
		};
	}
	return pendingDecision(fetch.body);
}

function pendingDecision(body: unknown): KristaPollDecision {
	const progress = readKristaEventProgress(body);
	return progress ? { outcome: 'pending', progress } : { outcome: 'pending' };
}

/**
 * Progress on a Krista event. `value` is 0–1. The sentence is
 * `progress.message`, then the event `message`, then `progress.label`.
 */
export function readKristaEventProgress(
	body: unknown,
): InsightRunProgress | null {
	if (!isRecord(body)) return null;
	const progress = body['progress'];
	const progressRecord = isRecord(progress) ? progress : null;
	const value = normalizeProgressValue(progressRecord?.['value']);
	const message =
		stringField(progressRecord?.['message']) ??
		stringField(body['message']) ??
		stringField(progressRecord?.['label']) ??
		null;
	if (value == null && !message) return null;
	return { value: value ?? 0, message };
}

function normalizeProgressValue(raw: unknown): number | null {
	if (typeof raw !== 'number' || !Number.isFinite(raw)) return null;
	const unit = raw > 1 ? raw / 100 : raw;
	return Math.min(1, Math.max(0, unit));
}

function stringField(value: unknown): string | null {
	return typeof value === 'string' && value.trim().length > 0
		? value.trim()
		: null;
}

export function isInsightClaimStale(
	updatedAtMillis: number,
	nowMillis: number,
): boolean {
	return nowMillis - updatedAtMillis >= InsightSubmitClaimMillis;
}

function resubmitOrGiveUp(pollAttempts: number): KristaPollDecision {
	if (pollAttempts >= InsightPollMaxAttempts) {
		return {
			outcome: 'failed',
			error: {
				code: null,
				message: 'Insight polling timed out',
			},
		};
	}
	return { outcome: 'resubmit' };
}

function isRetryablePollStatus(status: number): boolean {
	return RetryablePollStatuses.has(status) || status >= 500;
}

/** The stored event is gone. Submit the same payload for a new id. */
export function isUnknownKristaEvent(body: unknown): boolean {
	if (!isRecord(body)) return false;
	const status = body['status'];
	return typeof status === 'string' && status.toLowerCase() === 'unknown';
}

function isRecord(value: unknown): value is Record<string, unknown> {
	return typeof value === 'object' && value !== null;
}

function isKristaResponseStatus(value: unknown): value is KristaResponseStatus {
	return (
		typeof value === 'string' &&
		(KristaResponseStatuses as readonly string[]).includes(value)
	);
}

/** Documented narrative, or the same object nested under `response`. */
export function readKristaGenerateResponse(
	body: unknown,
): KristaGenerateResponse | null {
	const direct = narrativeFrom(body);
	if (direct) return direct;
	if (!isRecord(body)) return null;
	return narrativeFrom(parseResponseValue(body['response']));
}

function parseResponseValue(value: unknown): unknown {
	if (typeof value !== 'string') return value;
	const trimmed = value.trim();
	if (!trimmed.startsWith('{') && !trimmed.startsWith('[')) return value;
	try {
		return JSON.parse(trimmed) as unknown;
	} catch {
		return value;
	}
}

/** Status inside the event body or the second parse of `response`. */
export function readKristaResponseStatus(
	body: unknown,
): KristaResponseStatus | null {
	const direct = statusFrom(body);
	if (direct) return direct;
	if (!isRecord(body)) return null;
	return statusFrom(parseResponseValue(body['response']));
}

function statusFrom(value: unknown): KristaResponseStatus | null {
	if (!isRecord(value) || !isKristaResponseStatus(value['status'])) {
		return null;
	}
	return value['status'];
}

function narrativeFrom(value: unknown): KristaGenerateResponse | null {
	if (!isRecord(value) || !isKristaResponseStatus(value['status'])) {
		return null;
	}
	if (value['status'] === 'pass_2_waiting') return null;
	if (
		typeof value['text'] === 'string' ||
		Array.isArray(value['findings']) ||
		(TerminalWithoutNarrative as readonly string[]).includes(
			value['status'],
		)
	) {
		return toAppKristaGenerateResponse(value);
	}
	return null;
}

export function readKristaEventId(body: unknown): string | null {
	if (!isRecord(body)) return null;
	return eventIdField(body['eventId']);
}

function eventIdField(value: unknown): string | null {
	return typeof value === 'string' && value.length > 0 ? value : null;
}
